import type { ReactNode } from 'react';
import { Check, Tag, Target, X } from 'lucide-react';
import { KaivaIcon, KaivaWordmark } from '@/components/brand';
import { cn } from '@/lib/utils';

type SessionTopbarProps = {
  workspaceName?: string;
  sessionName: string;
  elapsedSeconds: number;
  isRecording?: boolean;
  focusMode?: boolean;
  onFocusModeToggle?: () => void;
  showLabels?: boolean;
  onLabelsToggle?: () => void;
  /** When > 0, the Accept all / Skip all batch buttons appear. */
  stagedCount?: number;
  onAcceptAll?: () => void;
  onSkipAll?: () => void;
  onFinish: () => void;
  isFinishing?: boolean;
};

function formatMmSs(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const m = Math.floor(safe / 60).toString().padStart(2, '0');
  const s = (safe % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function SessionTopbar({
  workspaceName = 'Workspace',
  sessionName,
  elapsedSeconds,
  isRecording = true,
  focusMode = false,
  onFocusModeToggle,
  showLabels = false,
  onLabelsToggle,
  stagedCount = 0,
  onAcceptAll,
  onSkipAll,
  onFinish,
  isFinishing = false,
}: SessionTopbarProps) {
  const hasStaged = stagedCount > 0;
  return (
    <header
      className="flex h-[52px] w-full flex-shrink-0 items-center justify-between px-[18px]"
      style={{
        background: 'hsl(var(--card))',
        color: 'hsl(var(--foreground))',
        borderBottom: '1px solid hsl(var(--border))',
      }}
    >
      {/* Left cluster: brand mark + crumb */}
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex items-center gap-2.5">
          <KaivaIcon size={26} />
          <KaivaWordmark size={17} tone="ink" />
        </div>
        <Separator />
        <div
          className="truncate text-[11.5px] tracking-[0.01em]"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          Workspace · <strong className="font-medium" style={{ color: 'hsl(var(--foreground))' }}>{workspaceName}</strong>{' '}
          / {sessionName}
        </div>
      </div>

      {/* Right cluster: recording pill, mode toggles, finish button */}
      <div className="flex items-center gap-3">
        <RecordingPill seconds={elapsedSeconds} isRecording={isRecording} />

        <Separator />

        <ModeToggle
          icon={<Target className="h-3 w-3" />}
          label="Focus mode"
          active={focusMode}
          onClick={onFocusModeToggle}
          title="Focus on the active proposal only"
        />
        <ModeToggle
          icon={<Tag className="h-3 w-3" />}
          label="Labels"
          active={showLabels}
          onClick={onLabelsToggle}
          title="Show node types and edge labels"
        />

        <Separator />

        {/* Batch staged actions — visible only when there are proposals to review */}
        {hasStaged && (
          <>
            <SecondaryButton onClick={onSkipAll} title="Skip every staged proposal">
              <X className="h-3 w-3" />
              Skip all
            </SecondaryButton>
            <PrimaryButton onClick={onAcceptAll} title="Accept every staged proposal">
              <Check className="h-3 w-3" />
              Accept all ({stagedCount})
            </PrimaryButton>
            <Separator />
          </>
        )}

        <FinishButton onClick={onFinish} disabled={isFinishing}>
          <Check className="h-3 w-3" />
          {isFinishing ? 'Finishing…' : 'Finish interview'}
        </FinishButton>
      </div>
    </header>
  );
}

function Separator() {
  return (
    <span
      aria-hidden="true"
      className="h-[18px] w-px"
      style={{ background: 'hsl(var(--border))' }}
    />
  );
}

function RecordingPill({
  seconds,
  isRecording,
}: {
  seconds: number;
  isRecording: boolean;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-[5px] text-[11.5px] tabular-nums"
      style={{
        background: 'hsl(var(--muted) / 0.5)',
        border: '1px solid hsl(var(--border))',
        color: 'hsl(var(--foreground))',
      }}
    >
      {isRecording && (
        <span
          aria-hidden="true"
          className="meeting-recording-dot inline-block h-1.5 w-1.5 rounded-full"
          style={{ background: 'hsl(var(--sienna))' }}
        />
      )}
      recording · {formatMmSs(seconds)}
    </span>
  );
}

function ModeToggle({
  icon,
  label,
  active = false,
  onClick,
  title,
}: {
  icon: ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={cn(
        'inline-flex h-[28px] items-center gap-1.5 rounded-lg px-2.5 text-[11.5px] font-medium transition-colors',
      )}
      style={
        active
          ? {
              background: 'hsl(var(--sienna) / 0.12)',
              color: 'hsl(var(--sienna-deep))',
              border: '1px solid hsl(var(--sienna) / 0.32)',
            }
          : {
              background: 'transparent',
              color: 'hsl(var(--muted-foreground))',
              border: '1px solid transparent',
            }
      }
    >
      {icon}
      {label}
    </button>
  );
}

function FinishButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-[28px] items-center gap-1.5 rounded-lg px-3 text-[12px] font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
      )}
      style={{
        background: 'hsl(var(--teal))',
        color: 'hsl(var(--slate-navy))',
        border: '1px solid hsl(var(--teal))',
      }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({
  onClick,
  title,
  children,
}: {
  onClick?: () => void;
  title?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex h-[28px] items-center gap-1.5 rounded-lg px-2.5 text-[11.5px] font-medium transition-colors"
      style={{
        background: 'hsl(var(--teal))',
        color: 'hsl(var(--slate-navy))',
        border: '1px solid hsl(var(--teal))',
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  onClick,
  title,
  children,
}: {
  onClick?: () => void;
  title?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex h-[28px] items-center gap-1.5 rounded-lg px-2.5 text-[11.5px] font-medium transition-colors hover:[background:hsl(var(--muted))]"
      style={{
        background: 'transparent',
        color: 'hsl(var(--muted-foreground))',
        border: '1px solid hsl(var(--border))',
      }}
    >
      {children}
    </button>
  );
}
